/**
 * authContext — replaces Redux auth slice
 * Provides: isAuthenticated, token, loading, initializing, error
 * Actions: signUpWithEmail, loginWithEmail, completeAuthSession, logout
 */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import authApi from '../api/auth/authApi';
import onboardingApi from '../api/onboarding/onboardingApi';
import { mapLoginPayload, mapRevokeTokenRequest, mapSignUpToCreateAccount } from '../api/auth/authMapper';
import { AUTH_EVENTS, ensureValidAccessToken } from '../api/auth/tokenRefresh';
import { subscribeToAppForeground } from '../platform/appLifecycle';
import { clearUserSessionData } from '../api/auth/clearUserSession';
import { resetSessionExpiredCoordinator } from '../api/auth/sessionExpired';
import { storage } from '../platform/storage';
import { getDeviceId, getDeviceTypeCode, getDeviceTypeLabel } from '../platform/device';
import { eventBus } from '../platform/eventBus';
import { getFCMToken } from '../platform/pushMessaging';
import { ToastService } from '../platform/notifier';
import { STORAGE_KEYS } from '../constants';
import {
  analytics,
  ANALYTICS_EVENTS,
  BRANCH_EVENTS,
  emitBranchEvent,
  resolveDistinctId,
  toAuthMethod,
  type AnalyticsIdentifyTraits,
} from '../analytics';
import { APP_ENV } from '../config/env';
import { useTranslation } from '../i18n/useTranslation';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { resolveAuthDisplayName } from '../utils/authDisplayName';
import {
  signInWithGoogle as googleSignIn,
  logoutGoogle as googleLogout,
  signInWithFacebook as fbSignIn,
  logoutFacebook as fbLogout,
  signInWithApple as appleSignIn,
  logoutApple as appleLogout,
} from '../platform/socialAuth';
import type {
  AuthContextValue,
  AuthState,
  LoginPayload,
  SignUpPayload,
  SocialAuthResult,
} from '../types';
import type { ApiError } from '../types/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const identifyAnalyticsUser = (
  userId: number | null | undefined,
  userGUID: string | null | undefined,
  traits?: Pick<
    AnalyticsIdentifyTraits,
    'email' | 'firstName' | 'userName'
  >,
): void => {
  const distinctId = resolveDistinctId(userGUID, userId);
  if (!distinctId) {
    return;
  }
  analytics.identify(distinctId, {
    userId: userId ?? null,
    userGuid: userGUID ?? null,
    platform: getDeviceTypeCode(),
    appEnv: APP_ENV,
    email: traits?.email ?? null,
    firstName: traits?.firstName ?? null,
    userName: traits?.userName ?? null,
  });
};

// ─── Initial State ────────────────────────────────────────────────────────────
const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  userId: null,
  userGUID: null,
  displayName: null,
  loading: false,
  initializing: true,
  error: null,
};

// ─── Reducer ──────────────────────────────────────────────────────────────────
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  RESTORE_SESSION: 'RESTORE_SESSION',
  LOGOUT: 'LOGOUT',
  SIGNUP_INIT: 'SIGNUP_INIT',
  TOKEN_REFRESHED: 'TOKEN_REFRESHED',
  CLEAR_ERROR: 'CLEAR_ERROR',
} as const;

interface AuthSuccessPayload {
  token: string;
  userId?: number | null;
  userGUID?: string | null;
  displayName?: string | null;
}

interface TokenRefreshedPayload {
  token: string;
}

interface SignUpInitPayload {
  token: string;
  userId: number;
  userGUID: string;
  displayName: string | null;
}

interface RestoreSessionPayload {
  token: string | null;
  userId: number | null;
  userGUID: string | null;
  displayName: string | null;
  hasRefreshToken?: boolean;
}

type AuthAction =
  | { type: typeof AUTH_ACTIONS.SET_LOADING; payload: boolean }
  | { type: typeof AUTH_ACTIONS.SET_ERROR; payload: string }
  | { type: typeof AUTH_ACTIONS.AUTH_SUCCESS; payload: AuthSuccessPayload }
  | { type: typeof AUTH_ACTIONS.SIGNUP_INIT; payload: SignUpInitPayload }
  | { type: typeof AUTH_ACTIONS.TOKEN_REFRESHED; payload: TokenRefreshedPayload }
  | { type: typeof AUTH_ACTIONS.RESTORE_SESSION; payload: RestoreSessionPayload }
  | { type: typeof AUTH_ACTIONS.LOGOUT }
  | { type: typeof AUTH_ACTIONS.CLEAR_ERROR };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload, error: null };
    case AUTH_ACTIONS.SET_ERROR:
      return { ...state, loading: false, error: action.payload };
    case AUTH_ACTIONS.AUTH_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        isAuthenticated: true,
        token: action.payload.token,
        userId: action.payload.userId ?? state.userId,
        userGUID: action.payload.userGUID ?? state.userGUID,
        displayName: resolveAuthDisplayName(
          state.displayName,
          action.payload.displayName,
        ),
      };
    case AUTH_ACTIONS.SIGNUP_INIT:
      return {
        ...state,
        loading: false,
        error: null,
        isAuthenticated: false,
        token: action.payload.token,
        userId: action.payload.userId,
        userGUID: action.payload.userGUID,
        displayName: action.payload.displayName,
      };
    case AUTH_ACTIONS.TOKEN_REFRESHED:
      return {
        ...state,
        token: action.payload.token,
      };
    case AUTH_ACTIONS.RESTORE_SESSION:
      return {
        ...state,
        initializing: false,
        isAuthenticated: Boolean(action.payload.token) || Boolean(action.payload.hasRefreshToken),
        token: action.payload.token,
        userId: action.payload.userId,
        userGUID: action.payload.userGUID,
        displayName: action.payload.displayName,
      };
    case AUTH_ACTIONS.LOGOUT:
      return { ...initialState, initializing: false };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    default:
      return state;
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────
const authContext = createContext<AuthContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export const AuthProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const { t } = useTranslation();
  const [sessionExpiredVisible, setSessionExpiredVisible] = useState(false);
  const sessionLogoutInProgressRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async (): Promise<void> => {
      let payload: RestoreSessionPayload = {
        token: null,
        userId: null,
        userGUID: null,
        displayName: null,
      };

      try {
        const token = await storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN);
        const refreshToken = await storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
        const userId = await storage.getItem<number>(STORAGE_KEYS.USER_ID);
        const userGUID = await storage.getItem<string>(STORAGE_KEYS.USER_GUID);
        const displayName = await storage.getItem<string>(STORAGE_KEYS.AUTH_DISPLAY_NAME);

        if (!token && !refreshToken) {
          await storage.removeItem(STORAGE_KEYS.USER_ID);
          await storage.removeItem(STORAGE_KEYS.USER_GUID);
          await storage.removeItem(STORAGE_KEYS.AUTH_DISPLAY_NAME);
          payload = { token: null, userId: null, userGUID: null, displayName: null };
        } else {
          let finalToken = token;
          try {
            const validatedToken = await Promise.race([
              ensureValidAccessToken(),
              new Promise<null>(resolve => setTimeout(() => resolve(null), 2000)),
            ]);
            if (validatedToken) {
              finalToken = validatedToken;
            }
          } catch {
            // If network fails, ignore and proceed with cached token to show offline state
          }
          payload = {
            token: finalToken,
            userId,
            userGUID,
            displayName,
            hasRefreshToken: Boolean(refreshToken),
          };
        }
      } catch {
        payload = { token: null, userId: null, userGUID: null, displayName: null };
      }

      if (cancelled) return;

      resetSessionExpiredCoordinator();
      dispatch({
        type: AUTH_ACTIONS.RESTORE_SESSION,
        payload,
      });
      if (payload.userId != null || payload.userGUID) {
        identifyAnalyticsUser(payload.userId, payload.userGUID);
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const signUpWithEmail = useCallback(async (payload: SignUpPayload) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    analytics.track(ANALYTICS_EVENTS.REGISTRATION_STARTED, { method: 'email' });
    try {
      const deviceId = await getDeviceId();
      const deviceToken = (await getFCMToken()) || undefined;
      const request = mapSignUpToCreateAccount(payload, deviceId, deviceToken);
      const { data } = await onboardingApi.createAccount(request);

      const { tokens, userGUID, userId } = data;
      const displayName =
        payload.username?.trim() ||
        payload.firstName?.trim() ||
        '';

      // Store credentials for subsequent onboarding API calls and session restore
      await storage.setItem(STORAGE_KEYS.AUTH_TOKEN, tokens.accessToken);
      await storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
      await storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, tokens.expiresAt);
      await storage.setItem(STORAGE_KEYS.USER_GUID, userGUID);
      await storage.setItem(STORAGE_KEYS.USER_ID, userId);
      if (displayName) {
        await storage.setItem(STORAGE_KEYS.AUTH_DISPLAY_NAME, displayName);
      }

      // SIGNUP_INIT keeps the navigator on Auth stack while onboarding continues
      dispatch({
        type: AUTH_ACTIONS.SIGNUP_INIT,
        payload: {
          token: tokens.accessToken,
          userId,
          userGUID,
          displayName: displayName || null,
        },
      });
      resetSessionExpiredCoordinator();
      identifyAnalyticsUser(userId, userGUID, {
        email: payload.email,
        firstName: payload.firstName,
        userName: payload.username,
      });
      analytics.track(ANALYTICS_EVENTS.REGISTRATION_COMPLETED, {
        method: 'email',
      });
      emitBranchEvent(BRANCH_EVENTS.REGISTRATION);
    } catch (error) {
      const errorMsg = getApiErrorMessage(error as ApiError);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: t(errorMsg) || errorMsg });
      throw error;
    }
  }, [t]);

  // ─── Login ────────────────────────────────────────────────────────────────

  const loginWithEmail = useCallback(
    async (payload: LoginPayload) => {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      try {
        const deviceId = await getDeviceId().catch(() => 'device_id_unknown');
        const deviceToken = (await getFCMToken().catch(() => null)) || undefined;
        const request = mapLoginPayload(payload, deviceId, deviceToken);
        const { data } = await authApi.login(request);

        const { tokens, userId, userGUID } = data;
        const identifier = (payload.email ?? payload.identifier ?? '').trim();
        const displayName =
          data.displayName?.trim() ||
          (!identifier.includes('@') ? identifier : '');

        await storage.setItem(STORAGE_KEYS.AUTH_TOKEN, tokens.accessToken);
        await storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
        await storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, tokens.expiresAt);
        await storage.setItem(STORAGE_KEYS.USER_ID, userId);
        if (userGUID) {
          await storage.setItem(STORAGE_KEYS.USER_GUID, userGUID);
        }
        if (displayName) {
          await storage.setItem(STORAGE_KEYS.AUTH_DISPLAY_NAME, displayName);
        } else {
          await storage.removeItem(STORAGE_KEYS.AUTH_DISPLAY_NAME);
        }

        dispatch({
          type: AUTH_ACTIONS.AUTH_SUCCESS,
          payload: {
            token: tokens.accessToken,
            userId,
            userGUID,
            displayName: displayName || null,
          },
        });
        resetSessionExpiredCoordinator();
        setSessionExpiredVisible(false);
        identifyAnalyticsUser(userId, userGUID, {
          email: identifier.includes('@') ? identifier : undefined,
          userName: identifier.includes('@') ? undefined : identifier || undefined,
        });
        try {
          analytics.track(ANALYTICS_EVENTS.LOGIN_COMPLETED, { method: 'email' });
          emitBranchEvent(BRANCH_EVENTS.LOGIN);
        } catch {
          /* ignore analytics tracking failure */
        }
      } catch (error) {
        const errorMsg = getApiErrorMessage(error as ApiError);
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMsg });
      }
    },
    [],
  );

  const completeAuthSession = useCallback(async (token?: string) => {
    const storedToken =
      token ??
      (await storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN)) ??
      stateRef.current.token;
    if (storedToken) {
      dispatch({
        type: AUTH_ACTIONS.AUTH_SUCCESS,
        payload: {
          token: storedToken,
          userId: stateRef.current.userId,
          userGUID: stateRef.current.userGUID,
          displayName: stateRef.current.displayName,
        },
      });
      resetSessionExpiredCoordinator();
      setSessionExpiredVisible(false);
      identifyAnalyticsUser(
        stateRef.current.userId,
        stateRef.current.userGUID,
      );
      analytics.track(ANALYTICS_EVENTS.ONBOARDING_COMPLETED);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      try {
        const refreshToken = await storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
        if (refreshToken) {
          const deviceId = await getDeviceId();
          const deviceType = getDeviceTypeLabel();
          const request = mapRevokeTokenRequest(refreshToken, deviceId, deviceType);
          await authApi.revokeToken(request);
        }
      } catch {
        /* ignore server errors on logout */
      }
      try { googleLogout(); } catch { /* ignore */ }
      try { fbLogout(); } catch { /* ignore */ }
      try { appleLogout(); } catch { /* ignore */ }
      try {
        await clearUserSessionData({ includeAuthTokens: true });
      } catch { /* ignore storage errors */ }
      try { resetSessionExpiredCoordinator(); } catch { /* ignore */ }
      try {
        analytics.track(ANALYTICS_EVENTS.LOGOUT);
        emitBranchEvent(BRANCH_EVENTS.LOGOUT);
        analytics.reset();
      } catch { /* ignore */ }
    } finally {
      // Always flip auth state — the session is dead regardless of any errors above.
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      setSessionExpiredVisible(false);
    }
  }, []);

  const confirmSessionExpiredAndLogout = useCallback(async () => {
    if (sessionLogoutInProgressRef.current) {
      return;
    }
    sessionLogoutInProgressRef.current = true;
    try {
      setSessionExpiredVisible(false);
      resetSessionExpiredCoordinator();
      await logout();
    } finally {
      sessionLogoutInProgressRef.current = false;
    }
  }, [logout]);

  // ─── Global Event Listeners ───────────────────────────────────────────────
  useEffect(() => {
    const sessionExpiredSubscription = eventBus.addListener(
      AUTH_EVENTS.SESSION_EXPIRED,
      () => {
        setSessionExpiredVisible(true);
      },
    );
    const recoverableRefreshFailureSubscription = eventBus.addListener(
      AUTH_EVENTS.REFRESH_FAILED_RECOVERABLE,
      (payload?: { message?: string; translationKey?: string }) => {
        const message =
          (payload?.translationKey ? t(payload.translationKey) : '') ||
          payload?.message ||
          t('auth.sessionRefreshFailed.message');
        ToastService.show({ type: 'error', message });
      },
    );
    const tokenRefreshSubscription = eventBus.addListener(
      AUTH_EVENTS.TOKEN_REFRESHED,
      (payload: { token?: string }) => {
        if (payload?.token) {
          dispatch({
            type: AUTH_ACTIONS.TOKEN_REFRESHED,
            payload: { token: payload.token },
          });
        }
      },
    );

    return () => {
      sessionExpiredSubscription.remove();
      recoverableRefreshFailureSubscription.remove();
      tokenRefreshSubscription.remove();
    };
  }, [logout, t]);

  useEffect(() => {
    return subscribeToAppForeground(() => {
      ensureValidAccessToken().catch(() => undefined);
    });
  }, []);

  const loginSSO = useCallback(
    async (
      provider: 'Google' | 'Apple' | 'Facebook',
      socialAuthToken: string,
      email?: string,
      userName?: string,
    ) => {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      try {
        if (!socialAuthToken || socialAuthToken.trim() === '') {
          const missingTokenMsg = 'Authentication token is missing. Please try again.';
          ToastService.show({ type: 'error', message: missingTokenMsg });
          dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: missingTokenMsg });
          throw new Error('missing_social_token');
        }

        const rawDeviceId = await getDeviceId().catch(() => 'device_id_unknown');
        const deviceId = rawDeviceId || 'device_id_unknown';
        const deviceType = getDeviceTypeLabel();
        const deviceToken = (await getFCMToken().catch(() => null)) || undefined;

        const payload: import('../api/auth/authTypes').LoginSSORequest = {
          socialAuthToken: socialAuthToken.trim(),
          signUpMethod: provider,
          deviceId,
          deviceType,
          deviceToken,
          ...(email?.trim() ? { email: email.trim() } : {}),
          ...(userName?.trim() ? { userName: userName.trim() } : {}),
        };

        const { data } = await authApi.loginSSO(payload);

        const { tokens, userId } = data;
        const userGUID = (data as { userGUID?: string }).userGUID;
        const displayName = data.displayName?.trim() || userName?.trim() || '';

        await storage.setItem(STORAGE_KEYS.AUTH_TOKEN, tokens.accessToken);
        await storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
        await storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, tokens.expiresAt);
        await storage.setItem(STORAGE_KEYS.USER_ID, userId);
        if (userGUID) {
          await storage.setItem(STORAGE_KEYS.USER_GUID, userGUID);
        }
        if (displayName) {
          await storage.setItem(STORAGE_KEYS.AUTH_DISPLAY_NAME, displayName);
        } else {
          await storage.removeItem(STORAGE_KEYS.AUTH_DISPLAY_NAME);
        }

        dispatch({
          type: AUTH_ACTIONS.AUTH_SUCCESS,
          payload: {
            token: tokens.accessToken,
            userId,
            userGUID,
            displayName: displayName || null,
          },
        });
        identifyAnalyticsUser(userId, userGUID, {
          email: email?.trim() || undefined,
          userName: userName?.trim() || undefined,
        });
        analytics.track(ANALYTICS_EVENTS.LOGIN_COMPLETED, {
          method: toAuthMethod(provider),
        });
        emitBranchEvent(BRANCH_EVENTS.LOGIN);
      } catch (error) {
        const errorMsg = getApiErrorMessage(error as ApiError);
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMsg });
        ToastService.show({ type: 'error', message: errorMsg });
        throw error;
      }
    },
    [],
  );

  const signUpSSO = useCallback(
    async (
      provider: 'Google' | 'Apple' | 'Facebook',
      socialAuthToken: string,
      email?: string,
      userName?: string,
      firstName?: string,
    ) => {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      const method = toAuthMethod(provider);
      analytics.track(ANALYTICS_EVENTS.REGISTRATION_STARTED, { method });
      try {
        if (!socialAuthToken || socialAuthToken.trim() === '') {
          const missingTokenMsg = 'Authentication token is missing. Please try again.';
          ToastService.show({ type: 'error', message: missingTokenMsg });
          dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: missingTokenMsg });
          throw new Error('missing_social_token');
        }

        const rawDeviceId = await getDeviceId();
        const deviceId = rawDeviceId || 'device_id_unknown';
        const deviceType = getDeviceTypeLabel();
        const deviceToken = (await getFCMToken()) || undefined;

        const payload: import('../api/onboarding/onboardingTypes').CreateAccountSSORequest = {
          socialAuthToken: socialAuthToken.trim(),
          signUpMethod: provider,
          deviceId,
          deviceType,
          deviceToken,
          ...(email?.trim() ? { email: email.trim() } : {}),
          ...(userName?.trim() ? { userName: userName.trim() } : {}),
          ...(firstName?.trim() ? { firstName: firstName.trim() } : {}),
        };

        const { data } = await onboardingApi.createAccountSSO(payload);

        const { tokens, userGUID, userId } = data;
        const displayName = userName?.trim() || firstName?.trim() || '';

        await storage.setItem(STORAGE_KEYS.AUTH_TOKEN, tokens.accessToken);
        await storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
        await storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, tokens.expiresAt);
        await storage.setItem(STORAGE_KEYS.USER_GUID, userGUID);
        await storage.setItem(STORAGE_KEYS.USER_ID, userId);
        if (displayName) {
          await storage.setItem(STORAGE_KEYS.AUTH_DISPLAY_NAME, displayName);
        }

        dispatch({
          type: AUTH_ACTIONS.SIGNUP_INIT,
          payload: {
            token: tokens.accessToken,
            userId,
            userGUID,
            displayName: displayName || null,
          },
        });
        identifyAnalyticsUser(userId, userGUID, {
          email: email?.trim() || undefined,
          firstName: firstName?.trim() || undefined,
          userName: userName?.trim() || undefined,
        });
        analytics.track(ANALYTICS_EVENTS.REGISTRATION_COMPLETED, { method });
        emitBranchEvent(BRANCH_EVENTS.REGISTRATION);
        return data;
      } catch (error) {
        const errorMsg = getApiErrorMessage(error as ApiError);
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: t(errorMsg) || errorMsg });
        ToastService.show({ type: 'error', message: errorMsg });
        throw error;
      }
    },
    [t],
  );

  // ─── Social Auth ──────────────────────────────────────────────────────────

  const signInWithGoogle = useCallback(
    (): Promise<SocialAuthResult> => googleSignIn() as Promise<SocialAuthResult>,
    [],
  );

  const signInWithFacebook = useCallback(
    (): Promise<SocialAuthResult> => fbSignIn() as Promise<SocialAuthResult>,
    [],
  );

  const signInWithApple = useCallback(
    (): Promise<SocialAuthResult> => appleSignIn() as Promise<SocialAuthResult>,
    [],
  );

  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);


  return (
    <authContext.Provider
      value={{
        ...state,
        signUpWithEmail,
        loginWithEmail,
        loginSSO,
        signUpSSO,
        completeAuthSession,
        logout,
        sessionExpiredVisible,
        confirmSessionExpiredAndLogout,
        clearError,
        signInWithGoogle,
        signInWithFacebook,
        signInWithApple,
      }}>
      {children}
    </authContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useAuth = (): AuthContextValue => {
  const auth = useContext(authContext);
  if (!auth) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return auth;
};

export default authContext;
